/**
 * Group Products React Query hooks.
 *
 * Day 14 §5.3.3 Group Products.
 */
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { groupProductsService } from "@/services/groupProductsService";
import type {
  CatalogFilters,
  BulkCreateInput,
} from "@/types/group-products.types";

const NS = ["group-products"] as const;
export const catalogKey = (filters: CatalogFilters = {}) =>
  [...NS, "list", filters] as const;
export const statsKey = () => [...NS, "stats"] as const;
export const categoriesKey = () => [...NS, "categories"] as const;

export function useGroupCatalog(filters: CatalogFilters = {}) {
  return useQuery({
    queryKey: catalogKey(filters),
    queryFn: () => groupProductsService.listCatalog(filters),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });
}

export function useCatalogStats() {
  return useQuery({
    queryKey: statsKey(),
    queryFn: () => groupProductsService.getStats(),
    staleTime: 60_000,
  });
}

export function useCatalogCategories() {
  return useQuery({
    queryKey: categoriesKey(),
    queryFn: () => groupProductsService.getCategories(),
    staleTime: 5 * 60 * 1000,
  });
}

export function useBulkCreateProducts() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: BulkCreateInput) =>
      groupProductsService.bulkCreate(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: NS }),
  });
}
